import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Email, Sms } from '@mui/icons-material'

import { preview } from '@/components/form/preview'
import { BackupEmailSubject } from '../../../../components/BackupEmailSubject'
import { NotificationPreview } from '../../../../components/NotificationPreview'

import { selectUserAccountPreferences } from '@/features/domain/user'
import { timeUtils } from '@/server-data'

import { useComputeSegmentsCount } from '@/hooks'
import type { TwoDaysNotificationValues } from '../../hooks/useTwoDaysNotificationValues'
import { useTexts } from './useTexts'

interface Props {
  values: TwoDaysNotificationValues
}

export function TwoDaysNotificationView(props: Props) {
  const { values } = props

  const accountPreferences = useSelector(selectUserAccountPreferences)
  const texts = useTexts()

  const notificationCutoffMins = useMemo(() => {
    return timeUtils.formatMinutesFromMidnight(
      values.notificationCutoffMins,
      accountPreferences.timeFormat,
    )
  }, [accountPreferences, values.notificationCutoffMins])

  const isEmailNotification = values.notificationWay === 'EMAIL'
  const isPhoneNotification = !isEmailNotification

  const notificationWay = values.notificationWay === 'PHONE' ? 'SMS' : 'Email'

  const segmentsCount = useComputeSegmentsCount(
    { ...values.templateDefaultLimits, ...values.templateCustomLimits },
    values.templateCustom,
    isEmailNotification
      ? values.includeMessageUnsubscriptionInstructionsEmail
      : values.includeMessageUnsubscriptionInstructionsPhone,
    values.includeMessageTrackingLink,
  )

  return (
    <>
      {/* Notification Time */}
      <preview.Field testId="notification-time" label={texts.notificationTime}>
        {notificationCutoffMins}
      </preview.Field>

      {/* Notification Type */}
      {isEmailNotification && (
        <NotificationPreview
          icon={<Email color="action" />}
          notificationType={notificationWay}
          subject={values.subjectCustom}
          templatePreview={values.templatePreview}
        />
      )}

      {isPhoneNotification && (
        <>
          {/* SMS Body */}
          <NotificationPreview
            icon={<Sms color="action" />}
            notificationType={notificationWay}
            templatePreview={values.templatePreview}
            additionalTextDetails={texts.segmentsCount(segmentsCount)}
          />

          {/* E-mail backup subject */}
          {values.backupNotification && (
            <BackupEmailSubject
              icon={<Email color="action" />}
              subject={values.backupSubjectCustom}
            />
          )}
        </>
      )}
    </>
  )
}
